import { FC, ReactNode } from 'react'
import Select, { ActionMeta, MultiValue, Props } from 'react-select'
import css from 'classnames'
import { useTranslation } from 'react-i18next'
import { SelectOption } from './types'

type P = Props<SelectOption, boolean> & {
  customLabel?: string | undefined | boolean | ReactNode
  value: MultiValue<SelectOption> | SelectOption | null | string
  isListOptions?: boolean
  disableSelectedRemoval?: boolean
  helperText?: ReactNode
  status?: 'warning' | 'error'
  variant?: 'secondary'
  labelType?: string | undefined | boolean
  isMultiCountries?: boolean
  allwaysOn?: boolean
  children?: ReactNode
  renderItemContent?: (item: SelectOption) => ReactNode
}

export const CustomSelect: FC<P> = ({
  value,
  options,
  onChange,
  onInputChange,
  className,
  isMulti,
  customLabel,
  isListOptions,
  disableSelectedRemoval,
  placeholder,
  helperText,
  status,
  variant,
  labelType,
  isDisabled,
  isLoading,
  noOptionsMessage,
  isMultiCountries,
  allwaysOn,
  children,
  renderItemContent,
  ...props
}) => {
  const { t } = useTranslation()

  const handleRemove = (id: string) => {
    const data =
      Array.isArray(value) && value?.filter((item) => item.value !== id)

    onChange?.(data, {} as ActionMeta<unknown>)
  }

  return (
    <div className="flex relative flex-col gap-1">
      {customLabel && labelType !== 'floating' ? (
        <span className="text-sm text-slate-500 ">{customLabel}</span>
      ) : null}
      {customLabel && labelType === 'floating' ? (
        <label
          className={css(
            'pointer-events-none absolute left-0 z-10 ml-2 mt-0.5 px-1 text-[10px] text-gray-400 duration-100 ease-linear',
          )}
        >
          {customLabel}
        </label>
      ) : null}
      <Select
        {...props}
        classNamePrefix="custom-select"
        isLoading={isLoading}
        isDisabled={isDisabled}
        options={options}
        value={value}
        onChange={(data, meta) => {
          onChange?.(data, meta)
        }}
        onInputChange={(e, action) => {
          if (action.prevInputValue !== e) {
            onInputChange?.(e, action)
          }
        }}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        className={css(className)}
        classNames={{
          control: () => css('relative'),
          singleValue: () => 'absolute top-2 mt-[-4px]',
          placeholder: () => 'absolute bottom-1 text-[14px] ',
          clearIndicator: () => 'hover:text-slate-300',
        }}
        noOptionsMessage={
          isLoading
            ? () => 'Загрузка...'
            : (noOptionsMessage ?? (() => `${t('dataNotFound')}!`))
        }
        styles={{
          multiValueLabel: (base) => ({
            ...base,
            display: isListOptions ? 'none' : 'flex',
          }),
          multiValue: (base) => ({
            ...base,
            display: isListOptions ? 'none' : 'flex',
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          multiValueRemove: (base) => ({
            ...base,
            display: isListOptions || disableSelectedRemoval ? 'none' : 'flex',
          }),

          placeholder: (base) => ({
            ...base,
            color: '#9CA3AF',
            marginTop: '10px',
          }),
          control: (base, state) => ({
            ...base,
            borderRadius: '8px',
            minHeight: '48px',

            boxShadow: state.isFocused ? 'none' : 'none',

            borderColor: '#D1D1D1 !important',

            ...(variant &&
              variant === 'secondary' && {
                background: 'rgb(243 244 246)',
                borderColor: '#D1D1D1 !important',
                borderBottomColor: '#616161 !important',
              }),

            ...(status === 'error' &&
              Array.isArray(value) &&
              value.length === 0 && {
                border: '1px solid rgba(220, 38, 38, 1) !important',
              }),
            ...(status === 'error' &&
              !value && {
                border: '1px solid rgba(220, 38, 38, 1) !important',
              }),
            ...(status === 'warning' &&
              Array.isArray(value) &&
              value.length === 0 && {
                border: '1px solid rgba(234, 179, 8, 1) !important',
              }),
            ...(status === 'warning' &&
              isMultiCountries && {
                border: '1px solid rgba(234, 179, 8, 1) !important',
              }),
          }),
          menuList: (base) => ({
            ...base,
            padding: 0,
          }),
          menu: (base) => {
            return {
              ...base,
              overflow: 'hidden',
              overflowY: 'auto',
            }
          },

          option: (base, state) => {
            return {
              ...base,
              background: state.isFocused ? 'rgba(165, 165, 165, 0.5)' : '',
              color: state.isFocused ? 'white' : '',
            }
          },
          clearIndicator: (base) => ({
            ...base,
            cursor: 'pointer',
            display: isListOptions ? 'none' : 'block',
          }),
        }}
        isMulti={isMulti}
      />

      {helperText && Array.isArray(value) && value.length === 0 ? (
        <span
          className={css('0 text-[12px]', {
            'text-red-500': status === 'error',
            'text-yellow-500': status === 'warning',
          })}
        >
          {helperText}
        </span>
      ) : null}

      {helperText && !Array.isArray(value) ? (
        <span
          className={css('0 text-[12px]', {
            'text-red-500': status === 'error',
            'text-yellow-500': status === 'warning',
          })}
        >
          {helperText}
        </span>
      ) : null}

      {isListOptions && Array.isArray(value) && value.length > 0 ? (
        <div className="flex flex-col gap-3 mt-4">
          {value.map((item: SelectOption) => (
            <div key={item.value} className="flex justify-between gap-12">
              <div className="flex items-center justify-between group hover:bg-gray-100 transition py-2 px-4 rounded w-full">
                <p className="text-[16px]">{item.label}</p>

                <span
                  onClick={() => handleRemove(item.value)}
                  className="text-[16px] px-2 cursor-pointer text-gray-600 hover:text-gray-800 opacity-0  group-hover:opacity-100"
                >
                  &times;
                </span>
              </div>
              {renderItemContent ? renderItemContent(item) : children}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
